/**
 * Maintenance Mode Utilities
 * Controls when the app shows a "scheduled maintenance" countdown overlay
 *
 * Set VITE_MAINTENANCE_END_TIME in env to activate:
 *   VITE_MAINTENANCE_END_TIME=2024-01-15T14:00:00-08:00
 *
 * The time should be in ISO 8601 format with timezone offset (PST = -08:00, PDT = -07:00)
 */

/**
 * Parse the maintenance end time from environment variable
 * Returns null if not set or invalid
 */
export function getMaintenanceEndTime(): Date | null {
  const envValue = import.meta.env.VITE_MAINTENANCE_END_TIME as string | undefined

  if (!envValue || envValue.trim() === '') {
    return null
  }

  try {
    const date = new Date(envValue)
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('[Maintenance] Invalid VITE_MAINTENANCE_END_TIME:', envValue)
      return null
    }
    return date
  } catch {
    console.warn('[Maintenance] Failed to parse VITE_MAINTENANCE_END_TIME:', envValue)
    return null
  }
}

/**
 * Check if maintenance mode is currently active
 * - Returns false if VITE_MAINTENANCE_END_TIME is not set
 * - Returns false if the end time has passed
 * - Returns false if localStorage bypass is enabled
 * - Returns true if we're currently in maintenance window
 */
export function isMaintenanceModeActive(): boolean {
  // Dev/admin bypass via localStorage
  if (typeof window !== 'undefined') {
    if (localStorage.getItem('xavior.bypassMaintenance') === 'true') {
      return false
    }
  }

  const endTime = getMaintenanceEndTime()
  if (!endTime) {
    return false
  }

  return Date.now() < endTime.getTime()
}

/**
 * Time remaining breakdown
 */
export interface TimeRemaining {
  hours: number
  minutes: number
  seconds: number
  totalMs: number
  isExpired: boolean
}

/**
 * Calculate time remaining until maintenance ends
 */
export function getTimeRemaining(endTime: Date): TimeRemaining {
  const totalMs = endTime.getTime() - Date.now()

  if (totalMs <= 0) {
    return { hours: 0, minutes: 0, seconds: 0, totalMs: 0, isExpired: true }
  }

  const hours = Math.floor(totalMs / (1000 * 60 * 60))
  const minutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((totalMs % (1000 * 60)) / 1000)

  return { hours, minutes, seconds, totalMs, isExpired: false }
}

/**
 * Format time remaining as a human-readable string
 * Examples: "2h 15m 32s", "5m 12s", "45s"
 */
export function formatTimeRemaining(time: TimeRemaining): string {
  if (time.isExpired) {
    return 'Now'
  }

  const parts: string[] = []

  if (time.hours > 0) {
    parts.push(`${time.hours}h`)
  }
  if (time.minutes > 0 || time.hours > 0) {
    parts.push(`${time.minutes}m`)
  }
  parts.push(`${time.seconds}s`)

  return parts.join(' ')
}

/**
 * Format the end time in user's local timezone
 * Example: "2:00 PM"
 */
export function formatEndTimeLocal(endTime: Date): string {
  return endTime.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Format the end date if it's not today
 * Example: "Jan 15" or "Tomorrow"
 */
export function formatEndDateLocal(endTime: Date): string | null {
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)

  const endDay = new Date(endTime)

  // Reset time parts for comparison
  today.setHours(0, 0, 0, 0)
  tomorrow.setHours(0, 0, 0, 0)
  endDay.setHours(0, 0, 0, 0)

  if (endDay.getTime() === today.getTime()) {
    return null // Same day, don't show date
  }

  if (endDay.getTime() === tomorrow.getTime()) {
    return 'Tomorrow'
  }

  return endTime.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })
}
